import { Component, OnInit, OnDestroy, HostListener, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { StaffApiService } from '../../../core/api/services';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { PatientBipInfoModel } from '../../../core/api/models';
import { AlertService } from '../../../shared/services/alert.service';
import { Editor, editorConfig } from '../../../shared/ckeditor/ckeditor-config';
import { tap } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';

@Component({
  selector: 'app-bip-additional-info',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, SelectButtonModule, CKEditorModule],
  templateUrl: './bip-additional-info.component.html',
  styleUrl: '../../supervisor-main.component.css'
})
export class BipAdditionalInfoComponent implements OnInit, OnDestroy {
  patientID = input.required<string>();
  loading = signal<boolean>(false);
  currentModel: PatientBipInfoModel | null = null;
  draftModel: PatientBipInfoModel | null = null;
  additionalInfo: string | null = null; // draft content
  initialAdditionalInfo: string = '';
  public Editor = Editor;
  public editorConfig = editorConfig;
  isCurrentView: boolean = true;
  useOfflineData = input<boolean>(false);
  showToggle = input<boolean>(true);
  private autoSaveHandle: any;
  canEditBip: boolean = false;

  constructor(private staffApi: StaffApiService, private alerts: AlertService, private offlineDataService: OfflineDataService, private authService: AuthService) { }

  // Normalize optional input to a boolean defaulting to true
  shouldShowToggle(): boolean {
    const value = this.showToggle();
    return (value ?? true) && this.canEditBip;
  }

  ngOnInit(): void {
    this.canEditBip = this.authService.canEditBip();
    if (!this.canEditBip) {
      this.isCurrentView = true;
    }
    const useOffline = this.useOfflineData() || this.offlineDataService.isInOfflineMode();
    if (useOffline) {
      this.loadDataOffline();
    } else {
      this.loadData();
    }

    // Auto-save every 30s when in Draft view and draft model exists
    if (this.canEditBip) {
      this.scheduleAutoSave();
    }
  }

  private scheduleAutoSave(): void {
    this.autoSaveHandle = setTimeout(() => {
      if (!this.isCurrentView && this.draftModel) {
        this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
      }
      this.scheduleAutoSave();
    }, 30000);
  }
  loadData() {
    this.loading.set(true);
    if (!this.canEditBip) {
      this.staffApi.GetCurrentPatientBIPInfo({ patientID: Number(this.patientID()) }).subscribe({
        next: (data) => {
          this.currentModel = data ?? null;
          this.draftModel = null;
          this.loading.set(false);
        },
        error: () => {
          this.currentModel = null;
          this.draftModel = null;
          this.loading.set(false);
        }
      });
      return;
    }
    // Load current and draft in parallel
    this.staffApi.GetCurrentPatientBIPInfo({ patientID: Number(this.patientID()) }).subscribe({
      next: (data) => {
        this.currentModel = data ?? null;
      },
      error: () => {
        this.currentModel = null;
      }
    });

    this.staffApi.GetNewPatientBIPInfo({ patientID: Number(this.patientID()) }).subscribe({
      next: (data) => {
        this.draftModel = data ?? null;
        if (data == null || !this.draftModel?.AdditionalInfo) {
          if (!this.draftModel) {
            this.draftModel = {};
          }
          this.draftModel.AdditionalInfo = "<h2 style=\"text-align:center;\"><strong>Additional Information</strong></h2><figure class=\"table\" style=\"float:left;\"><table class=\"ck-table-resized\" style=\"border-style:none;\"><colgroup><col style=\"width:100%;\"></colgroup><tbody><tr><td style=\"background-color:#d9d9d9;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><strong>Title</strong></td></tr><tr><td style=\"border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><strong>Information</strong></td></tr></tbody></table></figure><p><br>&nbsp;</p>";
        }
        this.additionalInfo = this.draftModel?.AdditionalInfo ?? null;
        this.initialAdditionalInfo = this.additionalInfo ?? '';
        this.loading.set(false);
      },
      error: () => {
        this.draftModel = null;
        this.initialAdditionalInfo = '';
        this.loading.set(false);
      }
    });
  }

  loadDataOffline() {
    this.loading.set(true);
    const data = this.offlineDataService.getOfflinePatientData(Number(this.patientID()));
    this.currentModel = data?.patientBipInfo ?? null;
    this.draftModel = null;
    this.additionalInfo = this.currentModel?.AdditionalInfo ?? null;
    this.initialAdditionalInfo = this.additionalInfo ?? '';
    this.loading.set(false);
  }

  getSaveObservable(reloadAfter: boolean = true) {
    if (!this.draftModel) return;
    this.draftModel.PatientID = Number(this.patientID());
    this.draftModel.AdditionalInfo = this.additionalInfo ?? null;
    return this.staffApi.SavePatientBIPInfo({ body: this.draftModel }).pipe(
      tap((saved) => {
        this.draftModel = saved;
        if (reloadAfter) {
          this.initialAdditionalInfo = this.draftModel?.AdditionalInfo ?? '';
        }
      })
    );
  }

  save() {
    if (!this.canEditBip) return;
    if (!this.draftModel) {
      this.alerts.error('No draft BIP exists for this patient.');
      return;
    }
    this.getSaveObservable()?.subscribe({
      next: (saved) => {
        this.alerts.success('Saved');
      },
      error: () => this.alerts.error('Failed to save')
    });
  }

  onAdditionalInfoChange(event: any): void {
    if (event.editor) {
      const data = event.editor.getData();
      this.additionalInfo = data;
    }
  }

  onIsCurrentViewChange(event: any): void {
    if (!this.canEditBip) {
      this.isCurrentView = true;
      return;
    }
    const movingToCurrent = event?.value === true && this.isCurrentView === false;
    if (movingToCurrent && this.draftModel) {
      this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
    }
    if (this.isCurrentView) {
      this.initialAdditionalInfo = this.additionalInfo ?? '';
    }
    this.isCurrentView = event.value;
  }

  ngOnDestroy(): void {
    // Final silent save when leaving page if in Draft
    if (!this.isCurrentView && this.draftModel) {
      this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
    }
    if (this.autoSaveHandle) {
      clearTimeout(this.autoSaveHandle);
      this.autoSaveHandle = null;
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: Event) {
    if (!this.canEditBip) return;
    if (!this.isCurrentView && this.draftModel) {
      this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
    }
  }
}


